import { useEffect, useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import clsx from 'clsx'
import { X } from 'lucide-react'
import {
  useDesignateReview,
  useDesignatedReviewMutationPending,
} from '@/hooks/coaching/useDesignatedReview'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import type { CurriculumNodeView } from '@/types/coaching'
import {
  buildLessonPlacementOptions,
  LessonPlacementPicker,
  type LessonPlacementOption,
} from './LessonPlacementPicker'

interface Props {
  learnerId: number
  reviewLessonId: number
  reviewLessonTitle: string
  initialAnchorLessonId?: number | null
  onClose: () => void
}

type AnchorOption = LessonPlacementOption

export function ReviewPlacementDialog({
  learnerId,
  reviewLessonId,
  reviewLessonTitle,
  initialAnchorLessonId = null,
  onClose,
}: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const library = useLibrary(learnerId)
  const designate = useDesignateReview(learnerId, reviewLessonId)
  const mutationPending = useDesignatedReviewMutationPending(learnerId, reviewLessonId)
  const [selectedAnchorId, setSelectedAnchorId] = useState<number | null>(initialAnchorLessonId)
  const [error, setError] = useState<string | null>(null)
  const options = useMemo(() => buildReviewAnchorOptions(library.data?.tree ?? []), [library.data])
  const reviewLessonLabel = useMemo(
    () => buildReviewLessonLabel(library.data?.tree ?? [], reviewLessonId, reviewLessonTitle),
    [library.data, reviewLessonId, reviewLessonTitle],
  )
  const effectiveAnchorId = options.some((option) => option.lessonId === selectedAnchorId)
    ? selectedAnchorId
    : options[0]?.lessonId ?? null

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const submit = async () => {
    if (effectiveAnchorId == null) return
    setError(null)
    try {
      await designate.mutateAsync({ anchorLessonId: effectiveAnchorId, position: 'BEFORE' })
      onClose()
    } catch (requestError) {
      const code = axios.isAxiosError(requestError)
        ? requestError.response?.data?.error?.code
        : null
      setError(code
        ? t(`common:apiError.${code}`, t('common:error.unknown'))
        : t('common:error.unknown'))
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('coaching:designatedReview.placement.title')}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {t('coaching:designatedReview.placement.title')}
            </h2>
            <p className="mt-1 text-sm font-semibold text-red-600">
              <ContentMetadataText text={t('coaching:designatedReview.placement.description', { lesson: reviewLessonLabel })} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('coaching:designatedReview.placement.close')}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="size-5" />
          </button>
        </header>

        {library.isLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {t('coaching:designatedReview.placement.loading')}
          </p>
        ) : options.length === 0 ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800" role="status">
            {t('coaching:designatedReview.placement.noAnchor')}
          </p>
        ) : (
          <LessonPlacementPicker
            id="review-placement-anchor"
            label={t('coaching:designatedReview.placement.anchorLabel')}
            options={options}
            value={effectiveAnchorId}
            onChange={setSelectedAnchorId}
          />
        )}

        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}

        <footer className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('coaching:designatedReview.placement.cancel')}
          </button>
          <button
            type="button"
            disabled={effectiveAnchorId == null || mutationPending}
            onClick={() => void submit()}
            className={clsx(
              'rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white',
              (effectiveAnchorId == null || mutationPending) && 'cursor-not-allowed opacity-50',
            )}
          >
            {mutationPending
              ? t('coaching:designatedReview.placement.saving')
              : t('coaching:designatedReview.placement.submit')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}

export function buildReviewAnchorOptions(tree: CurriculumNodeView[]): AnchorOption[] {
  return buildLessonPlacementOptions(tree, (status) => status === 'not_started')
}

function buildReviewLessonLabel(
  tree: CurriculumNodeView[],
  reviewLessonId: number,
  fallbackTitle: string,
): string {
  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        const lessonIndex = module.lessons.findIndex((lesson) => lesson.lessonId === reviewLessonId)
        if (lessonIndex === -1) continue
        return `[${module.chapterNumber}-${lessonIndex + 1}. ${module.lessons[lessonIndex].title}]`
      }
    }
  }
  return `[${fallbackTitle}]`
}
